/**
 * GRANSKNING AV ETT SVAR — vägen från JSON.parse in i kontraktets typer.
 *
 * VARFÖR DET INTE RÄCKER MED `as MeResponse`.
 * shared/types.ts säger det rakt ut i avsnitt 1: "Ett bart `as Minutes` /
 * `as Ore` / ... får INTE förekomma i klient- eller serverkod: allt som kommer
 * ur JSON.parse eller ur en D1-/DO-rad passerar dessa [konstruktorer]. Utan dem
 * slutar märkningen verka precis där den behövs — vid nätgränsen." Ett
 * `JSON.parse(text) as TSvar` är exakt det förbjudna: det GER typerna utan att
 * NÅGOT prövats, och första gången servern skickar `null` där en Millis
 * förväntades kraschar gränssnittet någonstans långt därifrån.
 *
 * DEN HÄR FILEN ÄR KLIENTENS ENDA STÄLLE MED ETT BART `as <id-marke>`.
 * Precis samma regel som worker/src/http/validate.ts sätter för servern:
 * "pröva formen först, märk sedan". Enheterna (Minutes, Ore, Millis,
 * WorkDate, IsoWeek ...) går genom kontraktets egna konstruktorer; id-märkena
 * har inga konstruktorer i shared/types.ts och får sina här, byggda på samma
 * sätt.
 *
 * SÅ HÄR ANVÄNDS DEN. En granskare är `(ravarde: unknown) => T`. Varje anrop i
 * api/klient.ts kräver en, så det går inte att läsa ett svar man inte granskat.
 * Bygg din med hjälparna nedan:
 *
 *     const granskaTodayRad: Granskare<TodayRow> = (v) => {
 *       const o = objekt(v, 'today.rad');
 *       return {
 *         account_id: accountId(o['account_id'], 'account_id'),
 *         minutes: minuter(o['minutes'], 'minutes'),
 *       };
 *     };
 *
 * Fältnamnet är med i varje anrop och hamnar i felets `orsak`, så att ett
 * trasigt svar går att felsöka utan att köras om.
 *
 * RETURTYPEN ÄR BEVISET. Skriver du `Granskare<TodayResponse>` och kontraktet
 * lägger till ett fält faller tsc här — vilket är hela poängen med att
 * klienten importerar shared/ i stället för att kopiera det.
 */

import {
  asHundredths,
  asIsoWeek,
  asMeters,
  asMilli,
  asMillis,
  asMinutes,
  asOre,
  asOrePer10Km,
  asOrePerHour,
  asOrePerUnit,
  asSeq,
  asVersion,
  asWorkDate,
} from '@delat/types';
import type {
  AccountId,
  CompanyId,
  CustomerId,
  Hundredths,
  InvoiceRunId,
  IsoWeek,
  Meters,
  Milli,
  Millis,
  Minutes,
  ObjectKey,
  OpId,
  Ore,
  OrePer10Km,
  OrePerHour,
  OrePerUnit,
  OrgNumber,
  PriceListId,
  Seq,
  TaskId,
  Version,
  WorkDate,
  WorkOrderId,
} from '@delat/types';
import { ID_MAX_LENGTH, ID_MIN_LENGTH } from './konstanter';

export type Granskare<T> = (ravarde: unknown) => T;

/**
 * Kastas när ett svar har fel form. Fångas i api/klient.ts och blir ett
 * Obegripligtfel — den når aldrig ut ur modulen som ett kast.
 */
export class Granskningsfel extends Error {
  readonly falt: string;
  constructor(falt: string, sags: string) {
    super(`${falt}: ${sags}`);
    this.name = 'Granskningsfel';
    this.falt = falt;
  }
}

const brist = (falt: string, sags: string): never => {
  throw new Granskningsfel(falt, sags);
};

/** Kör en av kontraktets as*-konstruktorer och översätter dess kast. */
const genom = <T>(bygg: (v: unknown) => T, varde: unknown, falt: string): T => {
  try {
    return bygg(varde);
  } catch (orsak) {
    return brist(falt, orsak instanceof Error ? orsak.message : String(orsak));
  }
};

/* --- Grundformer -------------------------------------------------------- */

export const objekt = (varde: unknown, falt: string): Record<string, unknown> =>
  typeof varde === 'object' && varde !== null && !Array.isArray(varde)
    ? (varde as Record<string, unknown>)
    : brist(falt, 'förväntade ett objekt');

export const text = (varde: unknown, falt: string): string =>
  typeof varde === 'string' ? varde : brist(falt, 'förväntade en sträng');

export const flagga = (varde: unknown, falt: string): boolean =>
  typeof varde === 'boolean' ? varde : brist(falt, 'förväntade true eller false');

/** Ett vanligt heltal utan märke — antal, längd, räknare. */
export const heltal = (varde: unknown, falt: string): number =>
  typeof varde === 'number' && Number.isSafeInteger(varde)
    ? varde
    : brist(falt, 'förväntade ett heltal');

/**
 * En av flera tillåtna strängar. Används för varje literalunion i kontraktet
 * (Role, WeekState, SubscriptionStatus, CustomerKind, MaterialUnit ...).
 * Skicka kontraktets egen konstant som `tillatna`, aldrig en handskriven lista:
 *
 *     enAv(o['role'], 'role', ALL_ROLES)
 */
export const enAv = <T extends string>(
  varde: unknown,
  falt: string,
  tillatna: readonly T[],
): T => {
  if (typeof varde !== 'string') return brist(falt, 'förväntade en sträng');
  const traff = tillatna.find((kandidat) => kandidat === varde);
  return traff ?? brist(falt, `otillåtet värde ${varde}`);
};

export const lista = <T>(varde: unknown, falt: string, av: Granskare<T>): readonly T[] => {
  if (!Array.isArray(varde)) return brist(falt, 'förväntade en lista');
  return varde.map((post, i) => {
    try {
      return av(post);
    } catch (orsak) {
      if (orsak instanceof Granskningsfel) {
        throw new Granskningsfel(`${falt}[${i}].${orsak.falt}`, orsak.message);
      }
      throw orsak;
    }
  });
};

/**
 * NULL ÄR ETT VÄRDE, undefined är ett saknat fält. Kontraktet skiljer på dem
 * med flit (SYNC_CONFLICT { server_version: null } betyder "raden finns inte",
 * och 0 vore en lögn), så granskaren gör det också.
 */
export const nullbar = <T>(varde: unknown, falt: string, av: Granskare<T>): T | null => {
  if (varde === null) return null;
  try {
    return av(varde);
  } catch (orsak) {
    // Fältnamnet HÄR är det yttre ('customer.email'); den inre granskaren
    // känner bara sitt eget. Utan ombytet står bara 'email' i felet.
    if (orsak instanceof Granskningsfel) throw new Granskningsfel(falt, orsak.message);
    throw orsak;
  }
};

export const valfri = <T>(varde: unknown, av: Granskare<T>): T | undefined =>
  varde === undefined ? undefined : av(varde);

/* --- Kontraktets enheter. Alla går genom shared/types.ts. --------------- */

export const minuter = (v: unknown, f: string): Minutes => genom(asMinutes, v, f);
export const oren = (v: unknown, f: string): Ore => genom(asOre, v, f);
export const orePerTimme = (v: unknown, f: string): OrePerHour => genom(asOrePerHour, v, f);
export const orePerMil = (v: unknown, f: string): OrePer10Km => genom(asOrePer10Km, v, f);
export const orePerEnhet = (v: unknown, f: string): OrePerUnit => genom(asOrePerUnit, v, f);
export const meter = (v: unknown, f: string): Meters => genom(asMeters, v, f);
export const tusendel = (v: unknown, f: string): Milli => genom(asMilli, v, f);
export const millisekunder = (v: unknown, f: string): Millis => genom(asMillis, v, f);
export const hundradelar = (v: unknown, f: string): Hundredths => genom(asHundredths, v, f);
export const version = (v: unknown, f: string): Version => genom(asVersion, v, f);
export const seq = (v: unknown, f: string): Seq => genom(asSeq, v, f);
export const arbetsdag = (v: unknown, f: string): WorkDate => genom(asWorkDate, v, f);
export const isovecka = (v: unknown, f: string): IsoWeek => genom(asIsoWeek, v, f);

/* --- Id-märken ---------------------------------------------------------
   shared/types.ts ger inga konstruktorer för id-typerna. Formen prövas mot
   samma regel som worker/src/http/validate.ts requireIdText: 20-40 tecken ur
   [A-Za-z0-9_-]. HÄR, och bara här, står det bara `as`.
   ----------------------------------------------------------------------- */

const idtext = (varde: unknown, falt: string): string => {
  if (typeof varde !== 'string') return brist(falt, 'förväntade ett id');
  if (varde.length < ID_MIN_LENGTH || varde.length > ID_MAX_LENGTH) {
    return brist(falt, `id-längden ${varde.length} ligger utanför ${ID_MIN_LENGTH}-${ID_MAX_LENGTH}`);
  }
  if (!/^[A-Za-z0-9_-]+$/.test(varde)) return brist(falt, 'id bär otillåtna tecken');
  return varde;
};

export const accountId = (v: unknown, f: string): AccountId => idtext(v, f) as AccountId;
export const companyId = (v: unknown, f: string): CompanyId => idtext(v, f) as CompanyId;
export const customerId = (v: unknown, f: string): CustomerId => idtext(v, f) as CustomerId;
export const workOrderId = (v: unknown, f: string): WorkOrderId => idtext(v, f) as WorkOrderId;
export const taskId = (v: unknown, f: string): TaskId => idtext(v, f) as TaskId;
export const invoiceRunId = (v: unknown, f: string): InvoiceRunId => idtext(v, f) as InvoiceRunId;
export const priceListId = (v: unknown, f: string): PriceListId => idtext(v, f) as PriceListId;
export const opId = (v: unknown, f: string): OpId => idtext(v, f) as OpId;

/** CHECK i schema/d1.sql: tio siffror. Bindestreck är det användaren skriver. */
export const orgnummer = (varde: unknown, falt: string): OrgNumber => {
  if (typeof varde !== 'string') return brist(falt, 'förväntade ett organisationsnummer');
  const siffror = varde.replace(/[\s-]/g, '');
  return /^\d{10}$/.test(siffror) ? (siffror as OrgNumber) : brist(falt, 'org.nr måste vara tio siffror');
};

/**
 * ObjectKey: '<company_id>/<kind>/<ulid>.<ext>'. FÖRSTA SEGMENTET ÄR EN
 * BEHÖRIGHETSGRÄNS (shared/types.ts) — både FILE_UPLOAD och FILE_DOWNLOAD
 * avvisar en nyckel vars första segment inte är sökvägens :company_id. Klienten
 * prövar det också, så att ett fel i vår egen nyckelbyggnad upptäcks här och
 * inte som ett UNKNOWN_REFERENCE från servern.
 */
export const objectKey = (varde: unknown, falt: string, forFirma?: CompanyId): ObjectKey => {
  const s = text(varde, falt);
  const delar = s.split('/');
  if (delar.length !== 3 || delar.some((d) => d === '')) {
    return brist(falt, "object_key måste ha formen '<company_id>/<kind>/<ulid>.<ext>'");
  }
  if (forFirma !== undefined && delar[0] !== forFirma) {
    return brist(falt, 'object_key hör till en annan firma');
  }
  return s as ObjectKey;
};

/** Bygger en ObjectKey. Enda stället klienten skapar en. */
export const nyObjectKey = (
  firma: CompanyId,
  slag: 'photo' | 'receipt',
  ulid: string,
  filandelse: string,
): ObjectKey => `${firma}/${slag}/${ulid}.${filandelse}` as ObjectKey;
